import { CopyIcon, ExternalLinkIcon } from 'lucide-react';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import { toast } from 'sonner';
import {
  Artifact,
  ArtifactAction,
  ArtifactActions,
  ArtifactContent,
  ArtifactHeader,
  ArtifactTitle,
} from '@/components/ai-elements/artifact';
import { cn } from '@/lib/utils';
import {
  formatStreamingMetricsLabel,
  getDocumentContent,
  getDocumentTitle,
  getDocumentWordCount,
} from '../document-artifact';
import type { ActiveSourceReference } from '../chat-experience';
import { ChatDocumentKindIcon } from './ChatDocumentKindIcon';
import { SourceReferences } from '@/components/chat/source-references';
import { ChatDocumentPreview } from './ChatDocumentPreview';

type ChatDocumentCardProps = {
  document: ChatDocumentDTO;
  sourceReference?: ActiveSourceReference | null;
  sourceReferences?: ActiveSourceReference[];
  onOpen: (
    document: ChatDocumentDTO,
    reference?: ActiveSourceReference | null,
    references?: ActiveSourceReference[] | null,
  ) => void;
};

export function ChatDocumentCard({
  document,
  onOpen,
  sourceReference = null,
  sourceReferences = [],
}: ChatDocumentCardProps) {
  const title = getDocumentTitle(document);
  const content = getDocumentContent(document).trim();
  const isStreaming = document.status === 'streaming';
  const description = isStreaming
    ? formatStreamingMetricsLabel(getDocumentWordCount(document))
    : (document.filename ?? document.mediaType ?? null);

  async function handleCopy() {
    if (!content) {
      toast.info('Aucun contenu à copier pour le moment.');
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
      toast.success('Contenu du document copié.');
    } catch {
      toast.error('Impossible de copier le contenu du document.');
    }
  }

  return (
    <Artifact
      className={cn(
        'group w-full max-w-xl rounded-[var(--radius-surface-2xl)] border-border/70 bg-card/90 transition hover:-translate-y-0.5 hover:border-border hover:shadow-[0_18px_40px_rgba(15,23,42,0.07)]',
        isStreaming && 'border-ring/60 shadow-[0_0_0_3px_color-mix(in_oklch,var(--ring)_18%,transparent)]',
      )}
      data-streaming={isStreaming ? 'true' : undefined}
    >
      <ArtifactHeader className="border-border/50 bg-muted/25">
        <button
          type="button"
          onClick={() => onOpen(document, sourceReference, sourceReferences)}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left outline-none transition focus-visible:ring-2 focus-visible:ring-ring/40"
          aria-label={`Ouvrir le panneau ${title}`}
          data-testid={sourceReference ? 'citation-chip' : undefined}
        >
          <ChatDocumentKindIcon
            className={cn(isStreaming && 'animate-pulse')}
            document={document}
            size="sm"
          />
          <div className="min-w-0">
            <ArtifactTitle className="truncate text-sm font-semibold">{title}</ArtifactTitle>
            {description ? (
              <p className="text-xs text-muted-foreground">{description}</p>
            ) : null}
            <SourceReferences className="mt-1" reference={sourceReference} />
          </div>
        </button>
        <ArtifactActions>
          <ArtifactAction
            icon={CopyIcon}
            label="Copier le document"
            onClick={(event) => {
              event.stopPropagation();
              void handleCopy();
            }}
            tooltip="Copier le contenu"
          />
          <ArtifactAction
            icon={ExternalLinkIcon}
            label="Ouvrir le panneau"
            onClick={(event) => {
              event.stopPropagation();
              onOpen(document, sourceReference, sourceReferences);
            }}
            tooltip="Ouvrir le panneau"
          />
        </ArtifactActions>
      </ArtifactHeader>

      <ArtifactContent className="p-0">
        <ChatDocumentPreview
          document={document}
          variant="card"
        />
      </ArtifactContent>
    </Artifact>
  );
}
